// The KYC review (TX-US-0035): its sections and rows as the screen names them. The reputation,
// financial and sector rows and the overall rating are the credit assessment's, and the General
// Counsel's review is the legal review (TX-US-0042); the review gains Compliance's answer on
// whether Risk sees the counterparty.
//
// The codes are the API's (trex/counterparties/kyc/scales.py); the labels are the form's
// (docs/KYC_KYV_Checklist_Scorecard_Report_20251227.docx). What a row's `kind` holds, and the
// steps between the API's sheet and the form's draft, are the shared rows' (../sheets/rows.js).

import { options, sheetOf } from '../sheets/rows.js';

const QUALITY = options(['HIGH', 'High'], ['MEDIUM', 'Medium'], ['LOW', 'Low']);

const check = (name, label) => ({ name, label, kind: 'check' });

export const SECTIONS = [
  {
    title: 'Compliance officer review',
    rows: [
      { name: 'officer_name', label: 'Compliance officer', kind: 'text' },
      { name: 'documents_received_on', label: 'Date of receipt of documents', kind: 'date' },
    ],
  },
  {
    title: 'Vendor / customer information',
    rows: [
      { name: 'nature_of_business', label: 'Nature of business', kind: 'text' },
    ],
  },
  {
    title: 'Document verification checklist',
    rows: [
      check('certificate_of_incorporation', 'Certificate of Incorporation (RCCM)'),
      check('tax_identification_number', 'Tax Identification Number'),
      check('articles_of_association', 'Articles of Association / Statuts'),
      check('signatory_identity', 'ID / passport of authorised signatory'),
      check('beneficial_owner_identity', 'ID / passport of beneficial owner(s)'),
      check('proof_of_address', 'Proof of operating address'),
      check('bank_account_details', 'Bank account details / RIB'),
      check('board_resolution', 'Board resolution / authority letter'),
      check('cnss_certificate', 'CNSS registration certificate'),
      check('professional_licence', 'Professional licence (if applicable)'),
      check('audited_financial_statements', 'Audited financial statements (3 years)'),
    ],
  },
  {
    title: 'Risk assessment scorecard',
    rows: [
      { name: 'document_completeness', label: 'Document completeness', kind: 'grade', options: QUALITY },
      {
        name: 'ownership_transparency',
        label: 'Beneficial ownership transparency',
        kind: 'grade',
        options: QUALITY,
      },
      {
        name: 'pep_screening',
        label: 'PEP screening',
        kind: 'grade',
        options: options(
          ['NO_PEP_LINKS', 'No PEP links'], ['INDIRECT_PEP', 'Indirect PEP'],
          ['DIRECT_PEP', 'Direct PEP'],
        ),
      },
      {
        name: 'sanctions_screening',
        label: 'Sanctions screening',
        kind: 'grade',
        options: options(
          ['CLEAR', 'Clear'], ['POTENTIAL_MATCH_RESOLVED', 'Potential match (resolved)'],
          ['MATCH_IDENTIFIED', 'Match identified'],
        ),
      },
    ],
  },
  {
    title: 'Compliance officer recommendation',
    rows: [
      {
        name: 'recommendation',
        label: 'Recommendation',
        kind: 'pick',
        options: options(['APPROVE', 'Approve vendor'], ['REJECT', 'Reject vendor']),
      },
      { name: 'rejection_reasons', label: 'Reasons for rejection', kind: 'longtext' },
      { name: 'conditions', label: 'Additional comments / conditions', kind: 'longtext' },
    ],
  },
  {
    title: 'Credit review',
    rows: [
      {
        name: 'credit_review',
        label: 'Credit review by Risk',
        kind: 'pick',
        options: options(['REQUIRED', 'Required'], ['NOT_REQUIRED', 'Not required']),
      },
      { name: 'credit_review_reason', label: 'Why no credit review is needed', kind: 'longtext' },
    ],
  },
];

const sheet = sheetOf(SECTIONS);

export const ROWS = sheet.rows;
export const { rowLabel, draftFrom, bodyFrom, draftErrors } = sheet;

// What the gate waits on is an answer, not only a row: a recommendation of Reject is recorded
// and is still not what KYC approval needs, and a document is received and verified.
const GATE_LABELS = {
  certificate_of_incorporation: 'Certificate of Incorporation: received and verified',
  tax_identification_number: 'Tax Identification Number: received and verified',
  professional_licence: 'Professional licence: received and verified, or N/A with a comment',
  recommendation: 'Recommendation: Approve vendor',
  credit_review: 'Credit review: required or not',
  credit_review_reason: 'Why no credit review is needed',
};

// What KYC approval is waiting on, as the screen names it.
export const missingLabels = (names = []) => names.map((name) => GATE_LABELS[name] ?? rowLabel(name));
